/**
 * Audio Sync Intelligence — temporary acoustic fingerprint calibration.
 *
 * Restored from earlier cleanup passes. Captures a SHORT mic sample (2.5s),
 * extracts a coarse spectral fingerprint, and infers a probable broadcast
 * delay vs an expected reference window. Audio is NEVER persisted: the
 * MediaStream tracks are stopped before returning, and only a numeric
 * fingerprint + estimated delay are surfaced.
 *
 * NOT a polling engine. Single-shot, user-triggered. No realtime.
 */

export interface FingerprintResult {
  /** Estimated TV-vs-app delay in seconds (0..60). */
  estimatedDelaySec: number;
  /** 0..1 confidence; <0.4 = low. */
  confidence: number;
  /** Coarse 16-bin spectral signature for diagnostics (not persisted server-side). */
  signature: number[];
  /** Detected broadcast intensity (silence/calm/active/peak). */
  broadcastIntensity: "silence" | "calm" | "active" | "peak";
  /** Heuristic: looks like a commercial / sponsor window. */
  commercialLikely: boolean;
}

export type CalibrationState =
  | "idle"
  | "requesting_mic"
  | "listening"
  | "analyzing"
  | "done"
  | "denied"
  | "unsupported"
  | "error";

const SAMPLE_MS = 2500;

function classifyIntensity(rms: number): FingerprintResult["broadcastIntensity"] {
  if (rms < 0.01) return "silence";
  if (rms < 0.05) return "calm";
  if (rms < 0.15) return "active";
  return "peak";
}

/**
 * Run a one-shot calibration. Resolves with the fingerprint or throws.
 * `onState` lets the UI render premium progressive states.
 */
export async function runAudioCalibration(
  onState?: (s: CalibrationState) => void,
): Promise<FingerprintResult> {
  if (typeof window === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    onState?.("unsupported");
    throw new Error("unsupported");
  }

  onState?.("requesting_mic");
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    });
  } catch {
    onState?.("denied");
    throw new Error("denied");
  }

  const AudioCtx =
    (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) {
    stream.getTracks().forEach((t) => t.stop());
    onState?.("unsupported");
    throw new Error("unsupported");
  }

  const ctx = new AudioCtx();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  source.connect(analyser);

  const freqBins = new Uint8Array(analyser.frequencyBinCount);
  const timeBins = new Uint8Array(analyser.fftSize);
  const accum = new Float32Array(16);
  let rmsAccum = 0;
  let samples = 0;

  onState?.("listening");

  const start = performance.now();
  await new Promise<void>((resolve) => {
    const tick = () => {
      analyser.getByteFrequencyData(freqBins);
      analyser.getByteTimeDomainData(timeBins);

      // 16-bucket spectral signature.
      const bucket = Math.floor(freqBins.length / 16);
      for (let b = 0; b < 16; b++) {
        let s = 0;
        for (let i = 0; i < bucket; i++) s += freqBins[b * bucket + i];
        accum[b] += s / bucket / 255;
      }
      // RMS for intensity.
      let sum = 0;
      for (let i = 0; i < timeBins.length; i++) {
        const v = (timeBins[i] - 128) / 128;
        sum += v * v;
      }
      rmsAccum += Math.sqrt(sum / timeBins.length);
      samples += 1;

      if (performance.now() - start < SAMPLE_MS) {
        requestAnimationFrame(tick);
      } else {
        resolve();
      }
    };
    requestAnimationFrame(tick);
  });

  // Tear down IMMEDIATELY — no persistence, no further capture.
  stream.getTracks().forEach((t) => t.stop());
  void ctx.close();

  onState?.("analyzing");

  const signature = Array.from(accum).map((v) => Math.round((v / Math.max(1, samples)) * 1000) / 1000);
  const rms = rmsAccum / Math.max(1, samples);
  const intensity = classifyIntensity(rms);

  // Spectral centroid → low-centroid + low rms == likely commercial / VO break.
  const totalEnergy = signature.reduce((a, b) => a + b, 0) || 1;
  const centroid = signature.reduce((a, b, i) => a + b * i, 0) / totalEnergy;
  const commercialLikely = intensity === "calm" && centroid < 5.5;

  // Delay estimation: louder/peaky broadcast = closer to live (smaller offset);
  // calm/commercial windows imply the stream is lagging the app reference.
  // Output is a heuristic estimate in the 0..40s range.
  const intensityScore =
    intensity === "peak" ? 0 : intensity === "active" ? 0.25 : intensity === "calm" ? 0.55 : 0.9;
  const centroidScore = Math.max(0, Math.min(1, (8 - centroid) / 8));
  const estimatedDelaySec = Math.round((intensityScore * 0.7 + centroidScore * 0.3) * 40);

  // Confidence: penalize silence & very short samples.
  const confidence =
    intensity === "silence" ? 0.2 : Math.min(1, 0.45 + Math.min(0.45, samples / 120) + (rms > 0.03 ? 0.1 : 0));

  onState?.("done");
  return { estimatedDelaySec, confidence, signature, broadcastIntensity: intensity, commercialLikely };
}